import React from 'react';
import { AlertCircle, ExternalLink } from 'lucide-react';

interface AnalysisErrorBannerProps {
  analysisType: string;
}

/**
 * Friendly error banner shown when an analysis output contains a parseError
 * (e.g. the LLM response was truncated and could not be parsed as valid JSON).
 */
export const AnalysisErrorBanner: React.FC<AnalysisErrorBannerProps> = ({ analysisType }) => (
  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 space-y-4">
    <div className="flex items-start gap-3">
      <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
      <div className="space-y-3">
        <h3 className="font-semibold text-red-900 dark:text-red-200 text-base">
          We're sorry — this analysis could not be completed
        </h3>
        <p className="text-sm text-red-800 dark:text-red-300 leading-relaxed">
          The {analysisType} response was too large to process correctly and arrived incomplete.
          This has been logged automatically and our support team has been notified. We will investigate this issue.
        </p>
        <p className="text-sm text-red-800 dark:text-red-300 leading-relaxed">
          Please try running the analysis again. If the problem persists, you can{' '}
          <a
            href={`mailto:support@clinlexis.co.uk?subject=${encodeURIComponent(analysisType + ' Error')}&body=${encodeURIComponent('An analysis failed with an incomplete response. Please investigate.')}`}
            className="inline-flex items-center gap-1 font-medium text-red-700 dark:text-red-200 underline hover:text-red-900 dark:hover:text-red-100"
          >
            contact support <ExternalLink className="w-3 h-3" />
          </a>{' '}
          for assistance.
        </p>
      </div>
    </div>
  </div>
);

/** Check whether an objective content object represents a backend parse failure. */
export function hasParseError(content: any): boolean {
  return !!(content?.parseError || content?.rawResponse);
}
